import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  Check,
  CircleDashed,
  KeyRound,
  LoaderCircle,
  Mic2,
  ShieldCheck,
  Database,
  Video,
  Volume2,
  Workflow,
} from "lucide-react";
import { api } from "../api";

const adapterMeta = {
  llm: {
    label: "LLM",
    description: "卖点拆解与三版脚本",
    icon: Bot,
    candidates: "OpenAI / Claude / 豆包 / 通义 / Dify",
  },
  video: {
    label: "Video",
    description: "生成式视频任务",
    icon: Video,
    candidates: "即梦 / 可灵 / Runway",
  },
  avatar: {
    label: "Avatar",
    description: "数字人口播节点",
    icon: Mic2,
    candidates: "即梦 OmniHuman / HeyGen",
  },
  tts: {
    label: "TTS",
    description: "配音与音频生成",
    icon: Volume2,
    candidates: "豆包语音 / ElevenLabs",
  },
  storage: {
    label: "Storage",
    description: "私有素材上传与临时签名 URL",
    icon: Database,
    candidates: "火山引擎 TOS / S3 / OSS",
  },
  creative_agent: {
    label: "Creative Agent",
    description: "审核后整条快速成片",
    icon: Workflow,
    candidates: "小云雀智能生视频 Agent",
  },
} as const;

export function AdaptersPage() {
  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: api.getConfig,
  });

  if (configQuery.isLoading) {
    return (
      <div className="state-panel">
        <LoaderCircle className="is-spinning" size={24} aria-hidden="true" />
        <strong>正在检查 Adapter 配置</strong>
      </div>
    );
  }
  if (configQuery.isError || !configQuery.data) {
    return (
      <div className="state-panel is-error">
        <CircleDashed size={24} aria-hidden="true" />
        <strong>{configQuery.error?.message ?? "无法读取配置"}</strong>
      </div>
    );
  }

  const config = configQuery.data;
  return (
    <div className="adapter-page">
      <section className="adapter-intro">
        <span className="section-index">Adapter Registry</span>
        <h2>工作流稳定，供应商可替换</h2>
        <p>
          统一任务模型隔离厂商差异。当前默认 Mock，不会因为本机存在 Key
          就自动发起付费请求。
        </p>
      </section>

      <section className="adapter-grid">
        {Object.entries(adapterMeta).map(([key, meta], index) => {
          const typedKey = key as keyof typeof adapterMeta;
          const state = config.adapters[typedKey];
          const Icon = meta.icon;
          return (
            <article className="adapter-card" key={key}>
              <div className="adapter-number">0{index + 1}</div>
              <div className="adapter-icon">
                <Icon size={23} strokeWidth={1.7} aria-hidden="true" />
              </div>
              <div className="adapter-copy">
                <span>{meta.label}</span>
                <h3>{state.provider}</h3>
                <p>{meta.description}</p>
              </div>
              <div className="adapter-state">
                {state.configured ? (
                  <span className={state.live_calls_enabled ? "configured" : ""}>
                    <Check size={14} aria-hidden="true" />
                    {state.live_calls_enabled ? "实时调用已启用" : "Key 已配置 / Dry-run"}
                  </span>
                ) : (
                  <span>
                    <CircleDashed size={14} aria-hidden="true" />
                    Mock / 未配置
                  </span>
                )}
              </div>
              <div className="candidate-line">{meta.candidates}</div>
            </article>
          );
        })}
      </section>

      <section className="provider-catalog">
        <div>
          <span>AVATAR FALLBACK</span>
          <h3>数字人主备链路</h3>
        </div>
        {config.provider_catalog.avatar.map((provider) => (
          <article key={provider.provider}>
            <strong>{provider.provider}</strong>
            <small>{provider.role === "primary" ? "主服务" : "备用服务"}</small>
            <span className={provider.configured ? "is-ready" : ""}>
              {provider.configured ? "凭证已配置" : "Adapter 已就绪 / 待配置 Key"}
            </span>
          </article>
        ))}
        <div>
          <span>TTS FALLBACK</span>
          <h3>配音主备链路</h3>
        </div>
        {config.provider_catalog.tts.map((provider) => (
          <article key={provider.provider}>
            <strong>{provider.provider}</strong>
            <small>{provider.role === "primary" ? "主服务" : "备用服务"}</small>
            <span className={provider.configured ? "is-ready" : ""}>
              {provider.configured ? "凭证已配置" : "Adapter 已就绪 / 待配置 Key"}
            </span>
          </article>
        ))}
      </section>

      <section className="security-board">
        <div className="security-title">
          <ShieldCheck size={24} aria-hidden="true" />
          <div>
            <span>SECURITY BOUNDARY</span>
            <h2>本地配置安全检查</h2>
          </div>
        </div>
        <div className="security-items">
          <div>
            <KeyRound size={18} aria-hidden="true" />
            <span>Key 不返回浏览器</span>
            <strong>{config.security.keys_exposed_to_browser ? "未通过" : "通过"}</strong>
          </div>
          <div>
            <ShieldCheck size={18} aria-hidden="true" />
            <span>路径限制在项目目录</span>
            <strong>{config.security.workspace_path_restriction ? "通过" : "未通过"}</strong>
          </div>
          <div>
            <CircleDashed size={18} aria-hidden="true" />
            <span>默认付费调用</span>
            <strong>
              {config.security.default_paid_calls_enabled ? "已启用" : "未启用"}
            </strong>
          </div>
        </div>
      </section>
    </div>
  );
}
